import { Select, Spin } from 'antd'
import React, { ComponentProps, useEffect } from 'react'
import { Bar } from 'react-chartjs-2'
import { useMunicipalityName, useVolumeAgeGroupValues } from '../../../../api/benchmarking'
import BenchmarkingContentRow from '../../components/BenchmarkingContentRow'
import { getFontSize } from '../../util/fontSize'
import { BASELINE_COLOR, getComparisonColor, MEDIAN_COLOR, QUARTILE_1_COLOR, QUARTILE_3_COLOR } from '../../util/colors'
import { BenchmarkingLegend } from '../../components/BenchmarkingLegend'
import { BORDER_WIDTH } from '../../util/constants'
import { baseGraphOptions } from '../../util/baseGraphOptions'

export const VolumeAgeGroup = ({ text }: { text: React.ReactNode }) => {
  const { data, isLoading, mutate } = useVolumeAgeGroupValues()
  const { data: municipalityName, isLoading: isMunicipalityNameLoading } = useMunicipalityName()
  useEffect(() => {
    mutate({})
  }, [])

  if (isLoading || isMunicipalityNameLoading) {
    return <BenchmarkingContentRow graph={<Spin size="large" />} text={text} />
  }

  if (!data) {
    return <></>
  }

  const options: ComponentProps<typeof Bar>['options'] = {
    ...baseGraphOptions,
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.datasetIndex === 0 ? municipalityName : context.dataset.label}: ${Math.round(context.parsed.y)}`
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            size: getFontSize('md'),
          },
        },
      },
      y: {
        ticks: {
          font: {
            size: getFontSize('md'),
          },
          callback: (value) => `${value}%`,
        },
      },
    },
  }

  //   export const BASELINE_COLOR = BLUE
  // export const QUARTILE_1_COLOR = CYAN
  // export const MEDIAN_COLOR = GREEN
  // export const QUARTILE_3_COLOR = YELLOW

  const graphData: ComponentProps<typeof Bar>['data'] = {
    labels: data.baselineValues.map((item) => item.ageRange),
    datasets: [
      {
        label: data.currentVariation.baseline?.name,
        data: data.baselineValues.map((item) => item.value),
        backgroundColor: BASELINE_COLOR.fill,
        borderColor: BASELINE_COLOR.border,
        borderWidth: BORDER_WIDTH,
      },
      {
        label: 'Undre kvartil',
        data: data.comparisonValues.map((item) => item.quartile1),
        backgroundColor: QUARTILE_1_COLOR.fill,
        borderColor: QUARTILE_1_COLOR.border,
        borderWidth: BORDER_WIDTH,
      },
      {
        label: 'Median',
        data: data.comparisonValues.map((item) => item.median),
        backgroundColor: MEDIAN_COLOR.fill,
        borderColor: MEDIAN_COLOR.border,
        borderWidth: BORDER_WIDTH,
      },
      {
        label: 'Övre kvartil',
        data: data.comparisonValues.map((item) => item.quartile3),
        backgroundColor: QUARTILE_3_COLOR.fill,
        borderColor: QUARTILE_3_COLOR.border,
        borderWidth: BORDER_WIDTH,
      },
    ],
  }

  const dataSourceSelector = (
    <Select
      value={data.currentDataSource}
      onChange={(value) => {
        mutate({ datasource: value, grouping: undefined })
      }}
      options={data.dataSources.map((ds) => ({
        label: ds,
        value: ds,
      }))}
      style={{ width: '100%' }}
    />
  )

  const comparisonSelector = (
    <Select
      value={data.currentGrouping}
      onChange={(value) => {
        mutate({ grouping: value, datasource: data.currentVariation.comparison?.datasource?.name })
      }}
      options={data.groupings.map((grouping) => ({
        label: grouping,
        value: grouping,
      }))}
      style={{ width: '100%' }}
    />
  )
  const legend = (
    <BenchmarkingLegend
      items={[
        { text: municipalityName, fillColor: BASELINE_COLOR.fill, borderColor: BASELINE_COLOR.border },

        { text: 'Undre kvartil', fillColor: QUARTILE_1_COLOR.fill, borderColor: QUARTILE_1_COLOR.border },
        { text: 'Median', fillColor: MEDIAN_COLOR.fill, borderColor: MEDIAN_COLOR.border },
        { text: 'Övre kvartil', fillColor: QUARTILE_3_COLOR.fill, borderColor: QUARTILE_3_COLOR.border },
      ]}
    />
  )

  const graph: React.ReactNode = <Bar options={options} data={graphData} />
  const title: string = 'Andel med insatser från äldreomsorgen i respektive åldersgrupp'
  return <BenchmarkingContentRow graph={graph} dataSourceSelector={dataSourceSelector} comparisonSelector={comparisonSelector} text={text} title={title} legend={legend} />
}
